import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { SparkleIcon, CheckIcon, PlusIcon, TrashIcon, ArrowIcon, CloseIcon } from './BespokeIcons';

/**
 * "I Choose To" Exercise - Based on NVC Chapter 8, Page 155
 * 
 * Helps users transform "I have to" thinking into "I choose to" thinking,
 * recognizing that every action is a choice made to meet our needs.
 */

const IChooseToExercise = ({ isOpen, onClose }) => {
  const [items, setItems] = useState([
    { id: 1, haveTo: '', needMet: '', chooseTo: '' }
  ]);
  const [step, setStep] = useState('intro'); // intro, exercise, reflection

  const addItem = () => {
    setItems([...items, { id: Date.now(), haveTo: '', needMet: '', chooseTo: '' }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const generateChooseTo = (item) => {
    if (item.haveTo && item.needMet) {
      return `I choose to ${item.haveTo.replace(/^(I have to |I must |I should |I need to )/i, '')} because I need ${item.needMet}.`;
    }
    return '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl bg-gradient-to-br from-blue-950 to-slate-950 border-blue-500/30 shadow-2xl">
        <CardHeader className="border-b border-blue-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
                <SparkleIcon size={24} color="#60A5FA" />
              </div>
              <div>
                <CardTitle className="text-blue-300 text-lg">The Power of Choice</CardTitle>
                <p className="text-blue-400/60 text-xs">Transform "have to" into "choose to"</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-blue-400/60 hover:text-blue-300"
            >
              <CloseIcon size={20} color="currentColor" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {step === 'intro' && (
            <div className="space-y-4 animate-fade-in">
              <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
                <p className="text-blue-200/90 text-sm leading-relaxed">
                  When we believe we have no choice, we often say phrases like:
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {["I have to...", "I must...", "I should...", "I can't help it...", "You should...", "I had no choice..."].map(phrase => (
                    <span key={phrase} className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-xs border border-red-500/30">
                      {phrase}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                <p className="text-emerald-200/90 text-sm leading-relaxed">
                  <span className="font-medium">The truth is:</span> Everything we do is a choice made to meet our needs. 
                  When we recognize this, we reclaim our power and act from a place of intention rather than obligation.
                </p>
              </div>

              <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                <p className="text-amber-200/90 text-sm italic leading-relaxed">
                  "When we choose to do things, we are more likely to proceed. 
                  When we feel we must do things, we automatically tend to resist."
                </p>
              </div>

              <Button 
                onClick={() => setStep('exercise')}
                className="w-full bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/40"
              >
                Begin the Exercise <ArrowIcon size={16} color="currentColor" direction="right" className="ml-2" />
              </Button>
            </div>
          )}

          {step === 'exercise' && (
            <div className="space-y-4 animate-fade-in">
              <p className="text-blue-300/80 text-sm mb-4">
                Think of things you do that you don't enjoy doing. For each one, identify the need it meets.
              </p>

              {items.map((item, index) => (
                <div key={item.id} className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-400/60 text-xs font-medium">#{index + 1}</span>
                    {items.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeItem(item.id)}
                        className="text-red-400/60 hover:text-red-300 h-6 w-6 p-0"
                      >
                        <TrashIcon size={14} color="currentColor" />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-red-300/70 text-xs mb-1 block">What I tell myself "I have to" do:</label>
                      <Input
                        value={item.haveTo}
                        onChange={(e) => updateItem(item.id, 'haveTo', e.target.value)}
                        placeholder="e.g., I have to go to work"
                        className="bg-red-500/10 border-red-500/30 text-white placeholder:text-red-300/30"
                      />
                    </div>

                    <div>
                      <label className="text-emerald-300/70 text-xs mb-1 block">The need this meets:</label>
                      <Input
                        value={item.needMet}
                        onChange={(e) => updateItem(item.id, 'needMet', e.target.value)}
                        placeholder="e.g., security, providing for family"
                        className="bg-emerald-500/10 border-emerald-500/30 text-white placeholder:text-emerald-300/30"
                      />
                    </div>

                    {item.haveTo && item.needMet && (
                      <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
                        <label className="text-amber-300/70 text-xs mb-1 block flex items-center gap-1">
                          <SparkleIcon size={12} color="#FBBF24" /> Transformation:
                        </label>
                        <p className="text-amber-200 text-sm font-medium">
                          {generateChooseTo(item)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <Button 
                onClick={addItem}
                variant="outline"
                className="w-full border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
              >
                <PlusIcon size={16} color="currentColor" className="mr-2" /> Add Another
              </Button>

              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={() => setStep('intro')}
                  variant="ghost"
                  className="flex-1 text-blue-400/60"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep('reflection')}
                  className="flex-1 bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 border border-amber-500/40"
                >
                  See My Choices <ArrowIcon size={16} color="currentColor" direction="right" className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 'reflection' && (
            <div className="space-y-4 animate-fade-in">
              <div className="text-center mb-6">
                <SparkleIcon size={40} color="#FBBF24" className="mx-auto mb-3" />
                <h3 className="text-xl text-amber-300 font-light">Your Choices</h3>
                <p className="text-amber-400/60 text-sm">Reclaim your agency</p>
              </div>

              <div className="space-y-3">
                {items.filter(item => item.haveTo && item.needMet).map((item, index) => (
                  <div key={item.id} className="bg-gradient-to-r from-amber-500/10 to-emerald-500/10 rounded-xl p-4 border border-amber-500/20">
                    <div className="flex items-start gap-3">
                      <CheckIcon size={20} color="#34D399" className="flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-red-300/60 text-xs line-through mb-1">{item.haveTo}</p>
                        <p className="text-amber-200 font-medium">{generateChooseTo(item)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20 mt-6">
                <p className="text-blue-200/90 text-sm leading-relaxed text-center">
                  When you recognize that every action is a choice made to meet your needs, 
                  you move from feeling controlled by external circumstances to feeling empowered by your own intentions.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={() => setStep('exercise')}
                  variant="ghost"
                  className="flex-1 text-blue-400/60"
                >
                  Edit
                </Button>
                <Button 
                  onClick={onClose}
                  className="flex-1 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40"
                >
                  <CheckIcon size={16} color="currentColor" className="mr-2" /> Complete
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IChooseToExercise;
